import { Head, router } from '@inertiajs/react'
import { Sparkles, Trash2, Plus, Check, X } from 'lucide-react'
import { useState } from 'react'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import type { CurrentUser } from '@/types/shared'

interface PromptConfig {
  id: string
  prompt_key: string
  model: string | null
  provider: string | null
  temperature: number | null
  enabled: boolean
  updated_at: string
}

interface Props {
  current_user: CurrentUser
  god_mode: boolean
  configs: PromptConfig[]
  known_prompt_keys: string[]
  providers: string[]
  default_model: string
  account_overrides_count: number
  configured_providers: Record<string, boolean>
}

export default function AIPromptConfigs({ current_user, configs, known_prompt_keys, providers, default_model, account_overrides_count, configured_providers }: Props) {
  const [creating, setCreating] = useState(false)

  return (
    <>
      <Head title="God Mode · AI Prompt Configs" />
      <MainLayout user={current_user} godMode breadcrumbs={[{ label: 'God Mode' }, { label: 'AI Prompts' }]}>
        <div className="p-6 max-w-5xl mx-auto space-y-6">
          <header>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Sparkles className="size-6 text-primary" /> Global AI prompt configs
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Defaults applied across every account when no per-account override exists. Currently <strong>{account_overrides_count}</strong> tenant-level overrides.
              Process-default model: <code className="text-xs">{default_model}</code>
            </p>
          </header>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configured providers</CardTitle>
              <CardDescription>From RubyLLM initializer — only providers with a key can be selected below.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(configured_providers).map(([name, ok]) => (
                  <span key={name} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs ${ok ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {ok ? <Check className="size-3" /> : <X className="size-3" />}
                    {name}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Prompt overrides</CardTitle>
                <CardDescription>Each prompt_key can have at most one global override.</CardDescription>
              </div>
              <Button onClick={() => setCreating(true)} className="cursor-pointer">
                <Plus className="size-4 mr-1" /> Add override
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {creating && <ConfigForm knownKeys={known_prompt_keys} providers={providers} onClose={() => setCreating(false)} />}

              {configs.length === 0 && !creating ? (
                <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                  No global overrides — every prompt resolves to the per-prompt frontmatter default or the process-default model.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {configs.map(c => <ConfigRow key={c.id} config={c} providers={providers} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    </>
  )
}

function ConfigRow({ config, providers }: { config: PromptConfig; providers: string[] }) {
  const [editing, setEditing] = useState(false)
  const remove = () => router.delete(`/god-mode/ai-prompts/${config.id}`, { preserveScroll: true })

  if (editing) {
    return <ConfigForm config={config} providers={providers} onClose={() => setEditing(false)} />
  }

  return (
    <div className="grid grid-cols-[1.5fr_1fr_1fr_80px_80px_80px] gap-3 items-center px-4 py-3 hover:bg-muted/30 text-sm">
      <div className="font-mono">{config.prompt_key}</div>
      <div>{config.model || <span className="text-muted-foreground">inherit</span>}</div>
      <div className="text-muted-foreground">{config.provider || 'auto'}</div>
      <div className="text-muted-foreground">t={config.temperature ?? '—'}</div>
      <div>
        <span className={`text-[10px] px-2 py-0.5 rounded ${config.enabled ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
          {config.enabled ? 'on' : 'off'}
        </span>
      </div>
      <div className="flex gap-1 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="cursor-pointer">Edit</Button>
        <Button variant="ghost" size="icon" onClick={remove} className="cursor-pointer">
          <Trash2 className="size-3.5 text-rose-400" />
        </Button>
      </div>
    </div>
  )
}

function ConfigForm({ config, knownKeys, providers, onClose }: { config?: PromptConfig; knownKeys?: string[]; providers: string[]; onClose: () => void }) {
  const [promptKey, setPromptKey] = useState(config?.prompt_key ?? (knownKeys?.[0] ?? ''))
  const [model, setModel] = useState(config?.model ?? '')
  const [provider, setProvider] = useState(config?.provider ?? '')
  const [temperature, setTemperature] = useState(config?.temperature?.toString() ?? '')
  const [enabled, setEnabled] = useState(config?.enabled ?? true)

  const submit = () => {
    const payload = {
      ai_prompt_config: {
        prompt_key: promptKey,
        model: model || null,
        provider: provider || null,
        temperature: temperature ? parseFloat(temperature) : null,
        enabled
      }
    }
    if (config) {
      router.patch(`/god-mode/ai-prompts/${config.id}`, payload, { preserveScroll: true, onSuccess: onClose })
    } else {
      router.post('/god-mode/ai-prompts', payload, { preserveScroll: true, onSuccess: onClose })
    }
  }

  return (
    <div className="px-4 py-4 bg-muted/30 border-b border-border space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="pk">Prompt key</Label>
          {config ? (
            <Input id="pk" value={promptKey} disabled />
          ) : (
            <select id="pk" value={promptKey} onChange={(e) => setPromptKey(e.target.value)}
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm">
              {(knownKeys ?? []).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          )}
        </div>
        <div>
          <Label htmlFor="m">Model</Label>
          <Input id="m" value={model} onChange={(e) => setModel(e.target.value)} placeholder="gemini-flash-lite-latest" />
        </div>
        <div>
          <Label htmlFor="p">Provider (optional)</Label>
          <select id="p" value={provider} onChange={(e) => setProvider(e.target.value)}
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm">
            <option value="">auto-detect</option>
            {providers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="t">Temperature</Label>
          <Input id="t" type="number" step="0.05" value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="0.3" />
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <label className="cursor-pointer flex items-center gap-2">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} className="cursor-pointer">Cancel</Button>
        <Button onClick={submit} className="cursor-pointer">Save</Button>
      </div>
    </div>
  )
}
